import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, ArrowRight } from "lucide-react";
export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left visual */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NzI5NDQ1OXww&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 to-slate-900/95" />
        <div className="relative z-10 flex items-center gap-3">
          <img
            src="/logo.png"
            alt="iRentals Serviced Accommodation"
            className="w-12 h-12 object-contain bg-white/95 p-1 rounded-sm"
          />
          <span className="font-heading text-xl font-semibold tracking-tight">iRentals</span>
        </div>
        <div className="relative z-10 space-y-6">
          <div className="label-eyebrow text-white/60">Serviced Accommodation Portal</div>
          <h1 className="font-heading text-5xl font-semibold tracking-tight leading-[1.05]">
            Track every stay.<br />
            Reconcile every payout.
          </h1>
          <p className="text-white/70 max-w-md">
            A precise workspace for serviced accommodation operators. Manage reservations,
            payments, and occupancy across your entire portfolio.
          </p>
        </div>
        <div className="relative z-10 text-xs text-white/50 label-eyebrow">© iRentals 2026</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md space-y-8" data-testid="login-form">
          <div className="space-y-2">
            <div className="label-eyebrow">Sign in</div>
            <h2 className="font-heading text-4xl font-semibold tracking-tight">Owner access</h2>
            <p className="text-sm text-slate-500">Use your credentials to enter the portal.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-eyebrow">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="username"
                className="rounded-sm border-zinc-300 focus:ring-2 focus:ring-slate-900 focus:border-slate-900 h-11"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="label-eyebrow">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                className="rounded-sm border-zinc-300 focus:ring-2 focus:ring-slate-900 focus:border-slate-900 h-11"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 text-white h-11 group active:scale-[0.98] transition-transform"
            data-testid="login-submit-button"
          >
            {loading ? "Signing in..." : (
              <span className="flex items-center justify-center gap-2">
                Sign in
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
            )}
          </Button>

          <div className="pt-6 border-t border-zinc-200 text-xs text-slate-500">
            Sign in with your owner credentials.
          </div>
        </form>
      </div>
    </div>
  );
}
